/**
 * Loads a sheet's page images: the small 's' bundle first (placeholders that are never removed),
 * then the device tier ('m' or 'l', upgrade-only) streamed page by page. Every page becomes an
 * object URL over a zero-copy Blob; all URLs are revoked on dispose (song closed / file changed).
 */
import { useEffect, useState, useSyncExternalStore } from 'react';
import type { SheetTier, SongFileDTO } from '../../shared/dto/library';
import { readBundleBlob, streamBundle } from './bundleStream';
import { openBundle } from './sources';
import { estimatePixels, tierRank } from './tiers';

export type LoadStatus = 'idle' | 'loading' | 'done' | 'error';

export interface TierImages {
  tier: SheetTier;
  status: LoadStatus;
  error: unknown;
  urls: (string | null)[];
  /** Raster size of each page at this tier (estimated until the bundle header arrives). */
  px: { pw: number; ph: number }[];
  loaded: number;
}

export interface SheetImages {
  s: TierImages;
  /** Large tiers that were requested, smallest first. */
  large: TierImages[];
}

type SheetFile = Pick<SongFileDTO, 'id' | 'rev' | 'pages' | 'tiers'>;

function emptyTier(tier: SheetTier, file: SheetFile, status: LoadStatus = 'idle'): TierImages {
  const pages = file.pages ?? [];
  return {
    tier,
    status,
    error: null,
    urls: pages.map(() => null),
    px: pages.map((p) => estimatePixels(tier, p)),
    loaded: 0,
  };
}

export class SheetImageLoader {
  private snap: SheetImages;
  private listeners = new Set<() => void>();
  private created = new Set<string>();
  private ctrl = new AbortController();
  private disposed = false;
  private notifyQueued = false;
  private queued: SheetTier[] = [];
  private running: SheetTier | null = null;

  constructor(private readonly file: SheetFile) {
    this.snap = { s: emptyTier('s', file), large: [] };
  }

  readonly subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  readonly getSnapshot = (): SheetImages => this.snap;

  get pageCount(): number {
    return this.file.pages?.length ?? 0;
  }

  start(): void {
    this.enqueue('s');
  }

  /** Ask for a large tier; ignored unless it is larger than every tier requested so far. */
  requestTier(tier: SheetTier | null): void {
    if (!tier || tier === 's' || !this.file.tiers?.[tier]) return;
    const known = [...this.snap.large.map((t) => t.tier), ...this.queued, ...(this.running ? [this.running] : [])];
    if (known.some((t) => tierRank(t) >= tierRank(tier))) return;
    this.enqueue(tier);
  }

  /** Re-run failed loads. */
  retry(): void {
    const failed = [this.snap.s, ...this.snap.large].filter((t) => t.status === 'error').map((t) => t.tier);
    for (const t of failed) this.enqueue(t);
  }

  dispose(): void {
    this.disposed = true;
    this.ctrl.abort();
    for (const u of this.created) URL.revokeObjectURL(u);
    this.created.clear();
    this.listeners.clear();
  }

  private enqueue(tier: SheetTier): void {
    if (this.disposed || this.running === tier || this.queued.includes(tier)) return;
    this.queued.push(tier);
    // 's' always goes first; larger tiers in ascending order.
    this.queued.sort((a, b) => tierRank(a) - tierRank(b));
    this.pump();
  }

  private pump(): void {
    if (this.disposed || this.running) return;
    // Large tiers wait until the placeholders are complete.
    const next = this.queued[0];
    if (!next) return;
    if (next !== 's' && this.snap.s.status !== 'done') return;
    this.queued.shift();
    this.running = next;
    void this.load(next).finally(() => {
      this.running = null;
      this.pump();
    });
  }

  private update(tier: SheetTier, fn: (t: TierImages) => TierImages): void {
    if (this.disposed) return;
    if (tier === 's') {
      this.snap = { ...this.snap, s: fn(this.snap.s) };
    } else {
      const idx = this.snap.large.findIndex((t) => t.tier === tier);
      const cur = idx >= 0 ? this.snap.large[idx]! : emptyTier(tier, this.file);
      const nextT = fn(cur);
      const large = idx >= 0 ? this.snap.large.map((t, i) => (i === idx ? nextT : t)) : [...this.snap.large, nextT];
      large.sort((a, b) => tierRank(a.tier) - tierRank(b.tier));
      this.snap = { ...this.snap, large };
    }
    this.scheduleNotify();
  }

  private scheduleNotify(): void {
    if (this.notifyQueued) return;
    this.notifyQueued = true;
    queueMicrotask(() => {
      this.notifyQueued = false;
      if (this.disposed) return;
      for (const fn of this.listeners) fn();
    });
  }

  private async load(tier: SheetTier): Promise<void> {
    const count = this.pageCount;
    const prev = tier === 's' ? this.snap.s : this.snap.large.find((t) => t.tier === tier);
    this.update(tier, (t) => ({ ...(prev ?? t), status: 'loading', error: null }));
    const have = new Set<number>();
    (prev?.urls ?? []).forEach((u, i) => u && have.add(i));
    const onPage = (i: number, blob: Blob) => {
      if (this.disposed || i >= count || have.has(i)) return;
      have.add(i);
      const url = URL.createObjectURL(blob);
      this.created.add(url);
      this.update(tier, (t) => {
        const urls = t.urls.slice();
        urls[i] = url;
        return { ...t, urls, loaded: t.loaded + 1 };
      });
    };
    const onHeader = (h: { pages: { pw: number; ph: number }[] }) => {
      this.update(tier, (t) => ({
        ...t,
        px: t.px.map((old, i) => {
          const p = h.pages[i];
          return p && p.pw > 0 && p.ph > 0 ? { pw: p.pw, ph: p.ph } : old;
        }),
      }));
    };
    try {
      const src = await openBundle(this.file.id, this.file.rev, tier, this.ctrl.signal);
      if (src instanceof Blob) await readBundleBlob(src, { onHeader, onPage });
      else if (src.body) await streamBundle(src.body, { onHeader, onPage }, this.ctrl.signal);
      else await readBundleBlob(await src.blob(), { onHeader, onPage });
      this.update(tier, (t) => ({ ...t, status: 'done' }));
    } catch (e) {
      if (this.disposed) return;
      this.update(tier, (t) => ({ ...t, status: 'error', error: e }));
    }
  }
}

const EMPTY: SheetImages = {
  s: { tier: 's', status: 'idle', error: null, urls: [], px: [], loaded: 0 },
  large: [],
};
const noopSubscribe = () => () => undefined;
const emptySnapshot = () => EMPTY;

/** React binding: one loader per (file id, rev); disposed (URLs revoked) on change/unmount. */
export function useSheetImages(file: SheetFile, wantedTier: SheetTier | null): { images: SheetImages; retry: () => void } {
  const [loader, setLoader] = useState<SheetImageLoader | null>(null);
  const key = `${file.id}:${file.rev}:${file.pages?.length ?? 0}`;
  useEffect(() => {
    const l = new SheetImageLoader(file);
    setLoader(l);
    l.start();
    return () => {
      l.dispose();
      setLoader((cur) => (cur === l ? null : cur));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  useEffect(() => {
    loader?.requestTier(wantedTier);
  }, [loader, wantedTier]);
  const images = useSyncExternalStore(loader ? loader.subscribe : noopSubscribe, loader ? loader.getSnapshot : emptySnapshot);
  return { images, retry: () => loader?.retry() };
}

/** Best large image of each page (largest loaded tier wins). */
export function bestLarge(images: SheetImages, pageCount: number): { url: string | null; tier: SheetTier | null; mp: number }[] {
  const out: { url: string | null; tier: SheetTier | null; mp: number }[] = [];
  for (let i = 0; i < pageCount; i++) {
    let url: string | null = null;
    let tier: SheetTier | null = null;
    let mp = 0;
    for (const t of images.large) {
      const u = t.urls[i];
      if (u) {
        url = u;
        tier = t.tier;
        const px = t.px[i];
        mp = px ? (px.pw * px.ph) / 1e6 : 0;
      }
    }
    out.push({ url, tier, mp });
  }
  return out;
}
